<template>
  <div :class="$style.welcomeContainer">
    <div :class="$style.welcomeContent">
      <!-- Header Section -->
      <div :class="$style.headerSection">
        <h1 :class="[$style.mainTitle, 'type-headline-large']">
          Ready to give your hiring process a quick health check?
        </h1>
        <p :class="$style.subtitle">
          Welcome to your personalized hiring audit! Over the next few pages,
          we'll ask a series of quick questions about your team's challenges,
          structure, and key metrics. Think of it as a quick check-up to find
          out what's working well and where there's room for improvement.
        </p>
      </div>

      <!-- Cards Section -->
      <div :class="$style.cardsSection">
        <!-- Benefits Card -->
        <div :class="$style.benefitsCard">
          <h2 :class="$style.benefitsTitle">
            Your reward for 15-20 minutes of your time is a powerful,
            data-driven report that includes:
          </h2>
          <ul :class="$style.benefitsList">
            <li :class="$style.benefitItem">
              <div :class="$style.checkIcon">
                <CheckIconComponent />
              </div>
              <span
                >A clear breakdown of your <strong>hiring strengths</strong> and
                <strong>biggest opportunities</strong> for improvement.</span
              >
            </li>
            <li :class="$style.benefitItem">
              <div :class="$style.checkIcon">
                <CheckIconComponent />
              </div>
              <span
                >Actionable next steps to
                <strong>solve your specific bottlenecks</strong> and start
                hiring more effectively, today.</span
              >
            </li>
            <li :class="$style.benefitItem">
              <div :class="$style.checkIcon">
                <CheckIconComponent />
              </div>
              <span
                >Your estimated <strong>Cost and Time-to-Hire</strong>,
                benchmarked against industry standards.</span
              >
            </li>
          </ul>
        </div>

        <!-- Info Cards -->
        <div :class="$style.infoCards">
          <div :class="$style.infoCard">
            <div :class="$style.infoIcon">
              <UserComponent />
            </div>
            <div :class="$style.infoContent">
              <h3 :class="$style.infoTitle">Who will lead the Audit?</h3>
              <p :class="$style.infoText">Recruiters and Hiring Managers</p>
            </div>
          </div>

          <div :class="$style.infoCard">
            <div :class="$style.infoIcon">
              <TimeComponent />
            </div>
            <div :class="$style.infoContent">
              <h3 :class="$style.infoTitle">How long will it take?</h3>
              <p :class="$style.infoText">15 - 20 minutes</p>
            </div>
          </div>

          <div :class="$style.infoCard">
            <div :class="$style.infoIcon">
              <AuditComponent />
            </div>
            <div :class="$style.infoContent">
              <h3 :class="$style.infoTitle">What do you get?</h3>
              <p :class="$style.infoText">
                Actionable insights and recommendations
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Mascot and Button -->
      <div :class="$style.bottomSection">
        <div :class="$style.mascotWrapper">
          <div :class="$style.speechBubble">Good start</div>
          <div :class="$style.mascotContainer">
            <yogenLogo
              :icon="yogenLogo"
              alt="Yogen Logo"
              :class="$style.mascot"
            />
          </div>
        </div>
        <Button
          :class="$style.startButton"
          text="Get Started"
          @click="handleStart"
          :variant="ButtonVariant.Primary"
          :size="ButtonSize.Large"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ButtonVariant, ButtonSize } from '~/constants/buttonTypes'
  import CheckIconComponent from '~/assets/svg/checkmarkCircle.svg'
  import UserComponent from '~/assets/svg/user.svg'
  import TimeComponent from '~/assets/svg/time.svg'
  import AuditComponent from '~/assets/svg/file.svg'
  import yogenLogo from '~/assets/svg/yogenLogo.svg'

  const emit = defineEmits<{
    start: []
  }>()

  const handleStart = () => {
    emit('start')
  }
</script>

<style module>
  .welcomeContainer {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    margin: 0 auto;
    padding: var(--space-32);
    gap: var(--space-40);
  }

  .welcomeContent {
    width: 100%;
  }

  .headerSection {
    text-align: left;
    margin-bottom: var(--space-32);
  }

  .mainTitle {
    color: var(--color-text-primary);
    margin: 0 0 var(--space-16) 0;
  }

  .subtitle {
    font: var(--type-body-medium);
    letter-spacing: var(--type-body-medium-tracking);
    color: var(--color-text-secondary);
    margin: 0;
  }

  .cardsSection {
    display: grid;
    grid-template-columns: 1.4fr 1fr;
    gap: var(--space-24);
    margin-bottom: var(--space-32);
  }

  .benefitsCard {
    background: var(--color-surface-default);
    border-radius: var(--radius-md);
    padding: var(--space-48);
    box-shadow: var(--elevation-md);
    outline: 1px var(--color-surface-muted) solid;
  }

  .benefitsTitle {
    font: var(--type-title-large);
    letter-spacing: var(--type-title-large-tracking);
    color: var(--color-text-primary);
    margin: 0 0 var(--space-24) 0;
  }

  .benefitsList {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-12);
  }

  .benefitItem {
    display: flex;
    align-items: flex-start;
    gap: var(--space-12);
    font: var(--type-body-large);
    letter-spacing: var(--type-body-large-tracking);
    color: var(--color-text-primary);
  }

  .benefitItem strong {
    font: var(--type-body-medium-emphasized);
    letter-spacing: var(--type-body-medium-emphasized-tracking);
  }

  .checkIcon {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
  }

  .checkIcon svg {
    width: 100%;
    height: 100%;
  }

  .infoCards {
    display: flex;
    flex-direction: column;
    gap: var(--space-16);
  }

  .infoCard {
    display: flex;
    align-items: center;
    gap: var(--space-20);
    background: var(--color-surface-default);
    border-radius: var(--radius-md);
    padding: var(--space-24);
    box-shadow: var(--elevation-md);
    outline: 1px var(--color-surface-muted) solid;
  }

  .infoIcon {
    width: 48px;
    height: 48px;
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: var(--color-surface-accent);
  }

  .infoIcon svg {
    width: 24px;
    height: 24px;
    color: var(--color-interactive-primary);
  }

  .infoContent {
    flex: 1;
  }

  .infoTitle {
    font: var(--type-title-medium-emphasized);
    letter-spacing: var(--type-title-medium-emphasized-tracking);
    color: var(--color-text-primary);
    margin: 0 0 var(--space-4) 0;
  }

  .infoText {
    font: var(--type-body-medium);
    letter-spacing: var(--type-body-medium-tracking);
    color: var(--color-text-secondary);
    margin: 0;
  }

  .bottomSection {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-16);
  }

  .mascotWrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .speechBubble {
    background-color: var(--color-surface-accent);
    color: var(--color-interactive-primary);
    padding: var(--space-8) var(--space-12);
    border-radius: var(--radius-md);
    font: var(--type-label-large-emphasized);
    letter-spacing: var(--type-label-large-emphasized-tracking);
    margin-bottom: var(--space-4);
    transform: rotate(-5deg);
    position: relative;
    z-index: 10;
  }

  .speechBubble::after {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 5px solid var(--color-surface-accent);
  }

  .mascot {
    width: 80px;
    height: auto;
  }

  .mascotContainer {
    filter: drop-shadow(var(--effect-brand-glow));
  }

  .startButton {
    width: 100%;
    max-width: 520px;
    padding: var(--space-16) var(--space-32);
    font: var(--type-title-small);
    letter-spacing: var(--type-title-small-tracking);
    color: var(--color-text-on-brand);
  }

  @media (max-width: 900px) {
    .cardsSection {
      grid-template-columns: 1fr;
    }

    .benefitsCard {
      padding: var(--space-24);
    }

    .infoCards {
      flex-direction: row;
      flex-wrap: wrap;
    }

    .infoCard {
      flex: 1;
      min-width: 200px;
    }
  }

  @media (max-width: 600px) {
    .welcomeContainer {
      padding: var(--space-16);
    }

    .infoCards {
      flex-direction: column;
    }

    .infoCard {
      min-width: auto;
    }
  }
</style>
